import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

// Define available languages
export type Language = 'english' | 'hindi' | 'marathi';

// Define language context type
interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

// Create the context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translations
const translations: Record<Language, Record<string, string>> = {
  english: {
    // Dashboard
    'welcome': 'Welcome',
    'select_service': 'Select a service to get started',
    'weather_station': 'Weather Station',
    'weather_desc': 'Get weather updates for your district and village',
    'dhartinetra': 'DhartiNetra',
    'dhartinetra_desc': 'Connect your DhartiNetra device for soil updates',
    'whatsapp_note': 'You will receive updates on WhatsApp after setting up your service',

    // Weather Station Form
    'weather_updates': 'Weather Updates',
    'get_weather_updates': 'Get weather updates on WhatsApp',
    'select_district': 'Select District',
    'select_village': 'Select Village',
    'select_district_first': 'Please select district first',
    'update_frequency': 'Update Frequency',
    'hourly': 'Hourly',
    'daily': 'Daily',
    'weekly': 'Weekly',
    'alerts_only': 'Alerts Only',
    'preferred_language': 'Preferred Language',
    'subscribe_weather': 'Subscribe to Weather Updates',
    'weather_note': 'You will receive weather updates on your registered WhatsApp number',

    // DhartiNetra Form
    'dhartinetra_device': 'DhartiNetra Device',
    'connect_soil_device': 'Connect your soil monitoring device',
    'device_id': 'DhartiNetra Device ID',
    'device_id_format': 'Format: DN-XXXXXX or DhartiNatra_XXXXXXXXXXXX',
    'device_id_help': 'You can find the Device ID on the back of your DhartiNetra device',
    'primary_crop': 'Primary Crop Type',
    'crop_placeholder': 'e.g. Cotton, Wheat, Sugarcane',
    'connect_device': 'Connect DhartiNetra Device',
    'soil_note': 'You will receive soil health updates on your registered WhatsApp number',

    // Settings
    'settings': 'Settings',
    'update_profile': 'Update your profile information',
    'your_name': 'Your Name',
    'name_placeholder': 'Enter your full name',
    'whatsapp_number': 'WhatsApp Number',
    'number_placeholder': '10-digit mobile number',
    'number_help': 'You will receive all updates on this WhatsApp number',
    'save_settings': 'Save Settings',
    'settings_note': 'Updating your WhatsApp number will change where you receive all notifications',

    // Common
    'home': 'Home',
    'back': 'Back',
    'save': 'Save',
    'logout': 'Logout',
    'menu': 'Menu',
  },

  hindi: {
    // Dashboard
    'welcome': 'स्वागत है',
    'select_service': 'शुरू करने के लिए एक सेवा चुनें',
    'weather_station': 'मौसम स्टेशन',
    'weather_desc': 'अपने जिले और गांव के लिए मौसम अपडेट प्राप्त करें',
    'dhartinetra': 'धरतीनेत्र',
    'dhartinetra_desc': 'मिट्टी अपडेट के लिए अपना धरतीनेत्र डिवाइस कनेक्ट करें',
    'whatsapp_note': 'सेवा सेटअप करने के बाद आपको WhatsApp पर अपडेट प्राप्त होंगे',

    // Weather Station Form
    'weather_updates': 'मौसम अपडेट',
    'get_weather_updates': 'WhatsApp पर मौसम अपडेट प्राप्त करें',
    'select_district': 'जिला चुनें',
    'select_village': 'गांव चुनें',
    'select_district_first': 'कृपया पहले जिला चुनें',
    'update_frequency': 'अपडेट की आवृत्ति',
    'hourly': 'प्रति घंटा',
    'daily': 'दैनिक',
    'weekly': 'साप्ताहिक',
    'alerts_only': 'केवल अलर्ट',
    'preferred_language': 'पसंदीदा भाषा',
    'subscribe_weather': 'मौसम अपडेट के लिए सदस्यता लें',
    'weather_note': 'आपको अपने पंजीकृत WhatsApp नंबर पर मौसम अपडेट प्राप्त होंगे',

    // DhartiNetra Form
    'dhartinetra_device': 'धरतीनेत्र डिवाइस',
    'connect_soil_device': 'अपना मिट्टी निगरानी उपकरण कनेक्ट करें',
    'device_id': 'धरतीनेत्र डिवाइस आईडी',
    'device_id_format': 'प्रारूप: DN-XXXXXX या DhartiNatra_XXXXXXXXXXXX',
    'device_id_help': 'आप अपने धरतीनेत्र डिवाइस के पीछे डिवाइस आईडी पा सकते हैं',
    'primary_crop': 'प्राथमिक फसल प्रकार',
    'crop_placeholder': 'जैसे कपास, गेहूं, गन्ना',
    'connect_device': 'धरतीनेत्र डिवाइस कनेक्ट करें',
    'soil_note': 'आपको अपने पंजीकृत WhatsApp नंबर पर मिट्टी स्वास्थ्य अपडेट प्राप्त होंगे',

    // Settings
    'settings': 'सेटिंग्स',
    'update_profile': 'अपनी प्रोफ़ाइल जानकारी अपडेट करें',
    'your_name': 'आपका नाम',
    'name_placeholder': 'अपना पूरा नाम दर्ज करें',
    'whatsapp_number': 'WhatsApp नंबर',
    'number_placeholder': '10-अंकों का मोबाइल नंबर',
    'number_help': 'आपको इस WhatsApp नंबर पर सभी अपडेट प्राप्त होंगे',
    'save_settings': 'सेटिंग्स सहेजें',
    'settings_note': 'अपना WhatsApp नंबर अपडेट करने से आपको सभी सूचनाएं प्राप्त होने का स्थान बदल जाएगा',

    // Common
    'home': 'होम',
    'back': 'वापस',
    'save': 'सहेजें',
    'logout': 'लॉगआउट',
    'menu': 'मेनू',
  },

  marathi: {
    // Dashboard
    'welcome': 'स्वागत आहे',
    'select_service': 'सुरू करण्यासाठी एक सेवा निवडा',
    'weather_station': 'हवामान स्टेशन',
    'weather_desc': 'तुमच्या जिल्हा आणि गावासाठी हवामान अपडेट्स मिळवा',
    'dhartinetra': 'धरतीनेत्र',
    'dhartinetra_desc': 'मातीच्या अपडेट्ससाठी तुमचे धरतीनेत्र डिव्हाइस कनेक्ट करा',
    'whatsapp_note': 'सेवा सेटअप केल्यानंतर तुम्हाला WhatsApp वर अपडेट्स मिळतील',

    // Weather Station Form
    'weather_updates': 'हवामान अपडेट्स',
    'get_weather_updates': 'WhatsApp वर हवामान अपडेट्स मिळवा',
    'select_district': 'जिल्हा निवडा',
    'select_village': 'गाव निवडा',
    'select_district_first': 'कृपया प्रथम जिल्हा निवडा',
    'update_frequency': 'अपडेट वारंवारता',
    'hourly': 'दर तासाला',
    'daily': 'दैनंदिन',
    'weekly': 'साप्ताहिक',
    'alerts_only': 'फक्त अलर्ट्स',
    'preferred_language': 'पसंतीची भाषा',
    'subscribe_weather': 'हवामान अपडेट्सचे सदस्यत्व घ्या',
    'weather_note': 'तुम्हाला तुमच्या नोंदणीकृत WhatsApp नंबरवर हवामान अपडेट्स मिळतील',

    // DhartiNetra Form
    'dhartinetra_device': 'धरतीनेत्र डिव्हाइस',
    'connect_soil_device': 'तुमचे माती मॉनिटरिंग डिव्हाइस कनेक्ट करा',
    'device_id': 'धरतीनेत्र डिव्हाइस आयडी',
    'device_id_format': 'फॉरमॅट: DN-XXXXXX किंवा DhartiNatra_XXXXXXXXXXXX',
    'device_id_help': 'तुम्ही तुमच्या धरतीनेत्र डिव्हाइसच्या मागे डिव्हाइस आयडी शोधू शकता',
    'primary_crop': 'प्राथमिक पीक प्रकार',
    'crop_placeholder': 'उदा. कापूस, गहू, ऊस',
    'connect_device': 'धरतीनेत्र डिव्हाइस कनेक्ट करा',
    'soil_note': 'तुम्हाला तुमच्या नोंदणीकृत WhatsApp नंबरवर मातीच्या आरोग्याचे अपडेट्स मिळतील',

    // Settings
    'settings': 'सेटिंग्ज',
    'update_profile': 'तुमची प्रोफाइल माहिती अपडेट करा',
    'your_name': 'तुमचे नाव',
    'name_placeholder': 'तुमचे पूर्ण नाव प्रविष्ट करा',
    'whatsapp_number': 'WhatsApp नंबर',
    'number_placeholder': '10-अंकी मोबाईल नंबर',
    'number_help': 'तुम्हाला या WhatsApp नंबरवर सर्व अपडेट्स मिळतील',
    'save_settings': 'सेटिंग्ज जतन करा',
    'settings_note': 'तुमचा WhatsApp नंबर अपडेट केल्याने तुम्हाला सर्व सूचना मिळण्याचे ठिकाण बदलेल',

    // Common
    'home': 'होम',
    'back': 'मागे',
    'save': 'जतन करा',
    'logout': 'लॉगआउट',
    'menu': 'मेनू',
  }
};

// Provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initialize with browser language or default to English
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language') as Language;
    const valid: Language[] = ['english', 'hindi', 'marathi'];
    return valid.includes(saved) ? saved : 'english';
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Translation function — falls back to English if language bucket is missing
  const t = (key: string): string => {
    const bucket = translations[language] ?? translations['english'];
    return bucket?.[key] ?? key;
  };

  // Set language function
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
  };

  const value = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
